<template>
  <label :class="[variant === 'static' ? '' : 'form-label', 'd-flex', 'align-items-center']" v-if="label">
    {{ label }}
    <span v-if="isRequired" class="text-danger">*</span>
    <Tooltip v-if="toolTipHeading && toolTipHeading != ''" :heading="toolTipHeading" :paragraph="toolTipDescription" />
  </label>

  <div class="input-group" :class="`input-group-${variant} ${getStatus(error, success)}`">
    <!-- Optional leading icon -->
    <span v-if="prependIcon" class="input-group-text">
      <i :class="prependIcon"></i>
    </span>

    <input
      :id="id || name"
      ref="dateInput"
      type="text"
      class="form-control datepicker flatpickr-input"
      :class="getClasses(size)"
      :name="name"
      :placeholder="placeholder"
      :required="isRequired"
      :readonly="readOnly"
      :disabled="disabled"
      :style="[readOnly && { cursor: 'pointer' }, style]"
    />
    <!-- Optional append slot -->
    <slot name="append"></slot>
  </div>
</template>

<script>
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Tooltip from "./Tooltip.vue";

export default {
  name: "MaterialDatePicker",
  components: { Tooltip },
  props: {
    modelValue: {
      type: [String, Date],
      default: "",
    },
    variant: {
      type: String,
      default: "outline",
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "default",
    },
    prependIcon: {
      type: String,
      default: "",
    },
    style: {
      type: [String, Object],
      default: "",
    },
    success: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: "",
    },
    id: {
      type: [String, undefined],
      default: undefined,
    },
    placeholder: {
      type: String,
      default: "Select date",
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    toolTipHeading: {
      type: String,
    },
    toolTipDescription: {
      type: String,
    },
    dateFormat: {
      type: String,
      default: "Y-m-d",
    },
    enableTime: {
      type: Boolean,
      default: false,
    },
    time_24hr: {
      type: Boolean,
      default: true,
    },
    minDate: {
      type: [String, Date],
      default: undefined,
    },
    maxDate: {
      type: [String, Date],
      default: undefined,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      flatpickrInstance: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initFlatpickr();
    });
  },
  beforeUnmount() {
    if (this.flatpickrInstance && typeof this.flatpickrInstance.destroy === 'function') {
      try {
        this.flatpickrInstance.destroy();
      } catch (error) {
        console.warn('Error destroying flatpickr instance:', error);
      }
      this.flatpickrInstance = null;
    }
  },
  watch: {
    modelValue(newValue) {
      if (this.flatpickrInstance) {
        try {
          if (newValue) {
            this.flatpickrInstance.setDate(newValue, false);
          } else {
            this.flatpickrInstance.clear();
          }
        } catch (error) {
          console.warn('Error updating flatpickr date:', error);
        }
      }
    },
    disabled(newValue) {
      if (this.flatpickrInstance && typeof this.flatpickrInstance.disable === 'function' && typeof this.flatpickrInstance.enable === 'function') {
        try {
          if (newValue) {
            this.flatpickrInstance.disable();
          } else {
            this.flatpickrInstance.enable();
          }
        } catch (error) {
          console.warn('Error toggling flatpickr disabled state:', error);
        }
      }
    },
  },
  methods: {
    initFlatpickr() {
      // Ensure the input element exists
      if (!this.$refs.dateInput) {
        console.warn('DatePicker input ref not found');
        return;
      }

      // Destroy existing instance if it exists
      if (this.flatpickrInstance && typeof this.flatpickrInstance.destroy === 'function') {
        try {
          this.flatpickrInstance.destroy();
        } catch (error) {
          console.warn('Error destroying existing flatpickr instance:', error);
        }
      }

      const options = {
        dateFormat: this.dateFormat,
        enableTime: this.enableTime,
        time_24hr: this.time_24hr,
        allowInput: false,
        clickOpens: !this.disabled, // Allow click to open even when readonly
        onChange: (selectedDates, dateStr) => {
          this.$emit("update:modelValue", dateStr);
        },
      };

      if (this.minDate) {
        options.minDate = this.minDate;
      }
      if (this.maxDate) {
        options.maxDate = this.maxDate;
      }

      try {
        this.flatpickrInstance = flatpickr(this.$refs.dateInput, options);

        // Set initial value
        if (this.modelValue) {
          this.flatpickrInstance.setDate(this.modelValue, false);
        }

        // If disabled, disable flatpickr
        if (this.disabled && typeof this.flatpickrInstance.disable === 'function') {
          this.flatpickrInstance.disable();
        }
      } catch (error) {
        console.error('Error initializing flatpickr:', error);
        this.flatpickrInstance = null;
      }
    },
    getClasses(size) {
      return size ? `form-control-${size}` : null;
    },
    getStatus(error, success) {
      if (success) return "is-valid";
      if (error) return "is-invalid";
      return null;
    },
  },
};
</script>

<style scoped>
.datepicker.flatpickr-input {
  background-color: #fff !important;
}

.datepicker.flatpickr-input[readonly] {
  cursor: pointer;
}
</style>

